import { useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';

import { FHButton, FHSideDrawer } from '../../../../components/base';
import TPActivationEditForm from './TPActivationEditForm';
import {
  formatActivationErrorMessage,
  getActivationLogEndDateChangeLimit,
  getActivationLogStartDateChangeLimit,
  getTimeScaleUnits,
} from './utils';
import TPRow from './TPRow';
import TimeScaleAxis from './TimescaleAxis';
import { treatmentPlanActions } from '../../../../store';

import type { Moment } from 'moment';

export type ActivationPeriod = {
  isActive: boolean;
  activationStart: string;
  activationStartId: number;
  activationEndId: number | null;
  activationEnd: string | null;
};

export type TPActivationItem = {
  tpId: number;
  isActive: boolean;
  status: string;
  startDate: string;
  activationsPeriods: ActivationPeriod[];
};

export type ActivationLimit = {
  date: string | null;
  reason: string | null;
};

type TPActivationChartProps = {
  selectedClientId?: number | string;
  selectedClientName?: string | null;
  startDate: string;
  endDate: string;
  activationLogLoading: boolean;
  tpActivationData: TPActivationItem[];
  timelineBackgroundColor?: string;
};

function TPActivationChart({
  selectedClientId,
  selectedClientName,
  startDate,
  endDate,
  activationLogLoading,
  tpActivationData,
  timelineBackgroundColor,
}: TPActivationChartProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTpId, setSelectedTpId] = useState<number | null>(null);
  const [selectedActivationPeriod, setSelectedActivationPeriod] = useState<ActivationPeriod | null>(
    null,
  );
  const [newActivationStartDate, setNewActivationStartDate] = useState<Moment | null>(null);
  const [newActivationEndDate, setNewActivationEndDate] = useState<Moment | null>(null);
  const [isStartDateChanged, setIsStartDateChanged] = useState(false);
  const [isEndDateChanged, setIsEndDateChanged] = useState(false);
  const [startDateUpdateError, setStartDateUpdateError] = useState<string | null>(null);
  const [endDateUpdateError, setEndDateUpdateError] = useState<string | null>(null);

  const [activationStartDateLimit, activationEndDateLimit] = useMemo(() => {
    let startDateChangeLimit: ActivationLimit = {
      date: null,
      reason: null,
    };
    let endDateChangeLimit: ActivationLimit = {
      date: null,
      reason: null,
    };
    if (!selectedTpId || !selectedActivationPeriod)
      return [startDateChangeLimit, endDateChangeLimit];

    startDateChangeLimit = getActivationLogStartDateChangeLimit(
      tpActivationData,
      selectedTpId,
      selectedActivationPeriod,
    );
    endDateChangeLimit = getActivationLogEndDateChangeLimit(
      tpActivationData,
      selectedTpId,
      selectedActivationPeriod,
    );
    return [startDateChangeLimit, endDateChangeLimit];
  }, [selectedActivationPeriod, tpActivationData, selectedTpId]);

  const handleBoxClick = (tpId: number, activationPeriod: ActivationPeriod) => {
    setSelectedTpId(tpId);
    setSelectedActivationPeriod(activationPeriod);
    setNewActivationStartDate(moment(activationPeriod.activationStart));
    setNewActivationEndDate(
      activationPeriod.activationEnd ? moment(activationPeriod.activationEnd) : null,
    );
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedTpId(null);
    setSelectedActivationPeriod(null);
    setNewActivationStartDate(null);
    setNewActivationEndDate(null);
    setIsStartDateChanged(false);
    setIsEndDateChanged(false);
    setStartDateUpdateError(null);
    setEndDateUpdateError(null);
  };

  const handleSelectStartDateTime = (value: Moment) => {
    if (!value) return;
    const val = value.seconds(59).milliseconds(0);

    const endLimit = newActivationEndDate
      ? moment(newActivationEndDate).subtract(1, 'minute')
      : activationEndDateLimit.date
        ? moment(activationEndDateLimit.date).subtract(1, 'minute')
        : null;

    if (activationStartDateLimit.date && val.isBefore(moment(activationStartDateLimit.date))) {
      setNewActivationStartDate(moment(activationStartDateLimit.date));
    } else if (endLimit && val.isAfter(endLimit)) {
      setNewActivationStartDate(moment(endLimit));
    } else {
      setNewActivationStartDate(val);
    }
    setIsStartDateChanged(true);
  };

  const handleSelectEndDateTime = (value: Moment) => {
    if (!value) return;
    const val = value.seconds(0).milliseconds(0);

    const startLimit = newActivationStartDate
      ? moment(newActivationStartDate).add(1, 'minute')
      : activationStartDateLimit.date
        ? moment(activationStartDateLimit.date).add(1, 'minute')
        : null;

    if (activationEndDateLimit.date && val.isAfter(moment(activationEndDateLimit.date))) {
      setNewActivationEndDate(moment(activationEndDateLimit.date));
    } else if (startLimit && val.isBefore(moment(startLimit))) {
      setNewActivationEndDate(moment(startLimit));
    } else {
      setNewActivationEndDate(val);
    }
    setIsEndDateChanged(true);
  };

  const handleUpdateActivationPeriod = async () => {
    if (!selectedTpId || !selectedActivationPeriod || !newActivationStartDate || !selectedClientId)
      return;
    const updateStartDate =
      isStartDateChanged &&
      newActivationStartDate.format('YYYY-MM-DDTHH:mm:ss') !==
        moment(selectedActivationPeriod.activationStart).format('YYYY-MM-DDTHH:mm:ss');
    const updateEndDate =
      isEndDateChanged &&
      selectedActivationPeriod.activationEnd !== null &&
      newActivationEndDate !== null &&
      newActivationEndDate.format('YYYY-MM-DDTHH:mm:ss') !==
        moment(selectedActivationPeriod.activationEnd).format('YYYY-MM-DDTHH:mm:ss');

    const activationUpdateList = [];

    if (updateStartDate) {
      activationUpdateList.push({
        activation_log_id: selectedActivationPeriod.activationStartId,
        date_changed: newActivationStartDate.toISOString(),
      });
    }
    if (updateEndDate) {
      activationUpdateList.push({
        activation_log_id: selectedActivationPeriod.activationEndId,
        date_changed: newActivationEndDate.toISOString(),
      });
    }

    if (!activationUpdateList.length) {
      toast.error('No changes to update.');
      return;
    }

    setEndDateUpdateError(null);
    setStartDateUpdateError(null);

    try {
      const response = await dispatch(
        treatmentPlanActions.updateTPActivationLogs({
          getAccessTokenSilently,
          data: activationUpdateList,
          treatmentPlanId: selectedTpId,
        }),
      );
      if (response.error) {
        const formattedError = formatActivationErrorMessage(response.error.message);
        if (formattedError.includes('DEACTIVATION:')) {
          setEndDateUpdateError(formattedError.replace('DEACTIVATION:', ''));
          toast.error('Changes not saved. Please fix the errors below and try again.');
        } else if (formattedError.includes('ACTIVATION:')) {
          setStartDateUpdateError(formattedError.replace('ACTIVATION:', ''));
          toast.error('Changes not saved. Please fix the errors below and try again.');
        }
      } else {
        handleDialogClose();
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const timeScale = getTimeScaleUnits(startDate, endDate);

  const filteredTPActivationData = tpActivationData.filter((tpData) => {
    if (!tpData.activationsPeriods.length) return false;
    const lastLog = tpData.activationsPeriods[tpData.activationsPeriods.length - 1];
    // If activationEnd date is null, it's still active
    if (!lastLog.activationEnd) return true;
    // If last activation date is after startDate, show that on the UI
    return moment(lastLog.activationEnd).isAfter(moment(startDate));
  });

  return (
    <Box sx={{ pt: 2 }}>
      <TimeScaleAxis timeScale={timeScale}>
        <Box sx={{ mt: 3 }}>
          {filteredTPActivationData.length > 0 ? (
            filteredTPActivationData.map((tp) => (
              <TPRow
                key={tp.tpId}
                tp={tp}
                startDate={startDate}
                endDate={endDate}
                onBoxClick={handleBoxClick}
                timelineBackgroundColor={timelineBackgroundColor}
              />
            ))
          ) : (
            <Typography>
              No active treatment plan found for the client in the selected date range.
            </Typography>
          )}
        </Box>
      </TimeScaleAxis>

      <FHSideDrawer
        sx={{ px: 6, py: 4, width: '900px' }}
        title={'Edit Activation Period'}
        titleStyles={{ fontSize: 32, pt: 0 }}
        open={dialogOpen}
        onClose={handleDialogClose}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ minHeight: 300 }}>
            {selectedTpId && selectedActivationPeriod && (
              <TPActivationEditForm
                activationLogLoading={activationLogLoading}
                clientName={selectedClientName ?? ''}
                startDateUpdateError={startDateUpdateError}
                endDateUpdateError={endDateUpdateError}
                startDate={newActivationStartDate}
                endDate={newActivationEndDate}
                selectedTPId={selectedTpId}
                selectedActivationPeriod={selectedActivationPeriod}
                tpActivationData={tpActivationData}
                activationStartDateLimit={activationStartDateLimit}
                activationEndDateLimit={activationEndDateLimit}
                setStartDateUpdateError={setStartDateUpdateError}
                setEndDateUpdateError={setEndDateUpdateError}
                handleSelectStartDateTime={handleSelectStartDateTime}
                handleSelectEndDateTime={handleSelectEndDateTime}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={handleDialogClose} variant="outlined" color="secondary" sx={{ mr: 1 }}>
              Cancel
            </Button>
            <FHButton
              onClick={handleUpdateActivationPeriod}
              variant="contained"
              color="primary"
              disabled={isStartDateChanged === false && isEndDateChanged === false}
            >
              Submit
            </FHButton>
          </Box>
        </Box>
      </FHSideDrawer>
    </Box>
  );
}

export default TPActivationChart;
